"use client";

import { createContext, useContext, type PropsWithChildren } from "react";

import {
  isActiveHopperProFromUserSubscriptionState,
  type UserSubscriptionBffState,
} from "../subscriptionStatusHelpers";
import { useShowSubscription } from "./FeatureFlagsContextProps";

export type SubscriptionStatusContextValue = {
  /**
   * Normalized subscription BFF `response` fragment (status + `isSubscriptionActive`),
   * e.g. from hopper-web-bff `get-user-subscription-state-with-session-id`.
   */
  subscriptionBffState?: UserSubscriptionBffState;
};

const SubscriptionStatusContext = createContext<SubscriptionStatusContextValue>(
  {}
);

export const SubscriptionStatusProvider = ({
  subscriptionBffState,
  children,
}: PropsWithChildren<SubscriptionStatusContextValue>) => (
  <SubscriptionStatusContext.Provider value={{ subscriptionBffState }}>
    {children}
  </SubscriptionStatusContext.Provider>
);

export const useUserSubscriptionBffState = () =>
  useContext(SubscriptionStatusContext).subscriptionBffState;

/**
 * Returns true if subscription UI should be shown.
 * Combines the subscription feature flag with actual subscription status check.
 */
export const useShouldShowSubscription = () => {
  const subscriptionBffState = useUserSubscriptionBffState();
  const showSubscriptionFlag = useShowSubscription();

  return (
    isActiveHopperProFromUserSubscriptionState(subscriptionBffState) ||
    showSubscriptionFlag
  );
};

/** @deprecated Use {@link useUserSubscriptionBffState} */
export const useSubscriptionStatusRaw = () =>
  useUserSubscriptionBffState()?.status;
